import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './CheckoutPage.module.css';
import usePricingCalculation from '../../hooks/usePricingCalculation';
import { computeOrderTotals } from '../../util/pricing';
import { initiateOrder } from './CheckoutPage.duck';
import { getProcess } from '../../transactions/transaction';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import EnhancedCalendar from './EnhancedCalendar';
import {
  getLiquorCatalog,
  getLiqueurCatalog,
  getCrownCatalog,
  getSodaCatalog,
  getSyrupCatalog,
  getChampagneCatalog,
  getBeerCatalog,
  getWineCatalog,
getSeltzerCatalog
} from '../../services/catalogService';

// CheckoutPage: captures event details, optional add-ons, summary of order, then triggers Sharetribe's "Request to Book"
import { storedData, STANDARD_STORAGE_KEYS } from './CheckoutPageSessionHelpers';
import { validateSessionStorageData } from '../../util/dataValidationHelpers';

// Import buildLineItems utility
import { buildLineItemsDetailed } from '../../util/lineItems';

const CheckoutPage = ({ onInitiateOrder, currentUser }) => {
  const history = useHistory();
  const location = useLocation();
    // Check for listing data FIRST, before any other processing
  const storedDataFromSession = (() => {
    try {
      // Try new standardized storage first
      const standardData = storedData(STANDARD_STORAGE_KEYS.CHECKOUT_DATA);
      if (standardData && Object.keys(standardData).length > 0) {
        // console.log('✅ CheckoutPage: Using standardized storage data');
        
        // Validate session storage data
        const validation = validateSessionStorageData(standardData);
        // console.log('🔍 CheckoutPage: Session data validation:', validation);
        
        if (!validation.isValid) {
          // console.warn('⚠️ CheckoutPage: Session data validation failed:', validation.errors);
        }
        
        return standardData;
      }
      
      // Fallback to legacy localStorage for backward compatibility
      const legacyData = JSON.parse(localStorage.getItem(STANDARD_STORAGE_KEYS.LEGACY_CHECKOUT_DATA)) || {};
      if (legacyData && Object.keys(legacyData).length > 0) {
        // console.log('⚠️ CheckoutPage: Using legacy localStorage data');
        return legacyData;
      }
      
      // console.log('❌ CheckoutPage: No stored data found');
      return {};
    } catch (error) {
      console.error('❌ CheckoutPage: Error retrieving stored data:', error);
      return {};
    }
  })();

  const listing = location.state?.listing || storedDataFromSession.listing;
  
  // Enhanced debug logging for data flow
  // console.log('🔍 CheckoutPage data sources:', {
  //   hasLocationState: !!location.state,
  //   hasStoredData: !!storedDataFromSession,
  //   hasListing: !!listing,
  //   locationStateKeys: location.state ? Object.keys(location.state) : [],
  //   storedDataKeys: Object.keys(storedDataFromSession),
  //   listingId: listing?.id?.uuid
  // });
    // Redirect if no listing data - BEFORE any hooks
  if (!listing) {
    return <NotFoundPage />;
  }

  // ─── ALL REACT HOOKS - MUST BE AT THE TOP ───
  // Catalog loading state
  const [catalogsLoaded, setCatalogsLoaded] = useState(false);
  const [allCatalogItemsById, setAllCatalogItemsById] = useState({});
  // Event details state
  const [eventDate, setEventDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [guestCount, setGuestCount] = useState(1);
  
  // Availability state for enhanced validation
  const [disabledDates, setDisabledDates] = useState([]);

  // Extract provider's availability plan from listing
  const availabilityPlan = useMemo(() => listing?.attributes?.availabilityPlan || null,
    [listing?.attributes?.availabilityPlan]);
  
  // (debug logging removed)

  // Helper: Check if a date is available according to the provider's availabilityPlan and API data
  function isDateAvailable(dateStr) {
    // Check against API availability data first (more accurate)
    if (disabledDates.length > 0 && disabledDates.includes(dateStr)) {
      return false;
    }
    
    // Fallback to listing availability plan
    if (!availabilityPlan || !availabilityPlan.entries) return true; // fallback: allow all
    const date = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = date.getDay(); // local DOW: 0=Sun..6=Sat
    // Support both numeric (0-6) and string ('sun'..'sat') dayOfWeek in entries
    const dayIndexToStr = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const matchesDay = e => {
      const dow = e?.dayOfWeek;
      if (typeof dow === 'number') return dow === dayOfWeek;
      if (typeof dow === 'string') return dow.toLowerCase() === dayIndexToStr[dayOfWeek];
      return false;
    };
    const entry = availabilityPlan.entries.find(matchesDay);
    return !!entry && entry.seats > 0;
  }

  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  // Submission state for inquiry-only flow
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Check if user is trying to book their own listing (Sharetribe blocks this with 409)
  const isOwnListing = !!(currentUser?.id?.uuid && listing?.author?.id?.uuid && currentUser.id.uuid === listing.author.id.uuid);

  // Helper function to validate event details
  const validateEventDetails = () => {
    const errors = {};
    const today = new Date();
    const selectedDate = new Date(eventDate);
    
    if (!eventDate) {
      errors.eventDate = 'Event date is required';
    } else if (selectedDate < today.setHours(0,0,0,0)) {
      errors.eventDate = 'Event date cannot be in the past';
    }
    
    if (!startTime) {
      errors.startTime = 'Start time is required';
    }
    
    if (!endTime) {
      errors.endTime = 'End time is required';
    } else if (startTime && endTime) {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);
      
      let startMinutes = startHour * 60 + startMin;
      let endMinutes = endHour * 60 + endMin;
      
      // Handle same-day events
      if (endMinutes <= startMinutes && endHour <= startHour) {
        errors.endTime = 'End time must be after start time (or specify if overnight)';
      }
      
      // Calculate duration for helpful feedback
      if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60; // Add 24 hours for overnight calculation
      }
      
      const durationMinutes = endMinutes - startMinutes;
      const durationHours = durationMinutes / 60;
      
      if (durationHours > 12) {
        errors.endTime = 'Events longer than 12 hours may require special arrangements';
      } else if (durationHours < 1) {
        errors.endTime = 'Events should be at least 1 hour long';
      }
    }
    
    if (!eventLocation.trim()) {
      errors.eventLocation = 'Event location is required';
    }
    
    if (guestCount < 1) {
      errors.guestCount = 'At least 1 guest is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };  // Set minimum date to today
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Generate time options for dropdown (24-hour format, 1-hour intervals)
  const generateTimeOptions = () => {
    const times = [];
    times.push({ value: '', label: 'Select time...' }); // Default option
    
    for (let hour = 0; hour < 24; hour++) {
      const time24 = `${hour.toString().padStart(2, '0')}:00`;
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const time12 = `${hour12}:00 ${ampm}`;
      
      times.push({
        value: time24,
        label: time12
      });
    }
    
    return times;
  };

  const timeOptions = generateTimeOptions();

  // Helper function to calculate duration between start and end time
  const calculateDuration = (start, end) => {
    if (!start || !end) return '';
    
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    
    let startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;
    
    // Handle overnight events
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60; // Add 24 hours
    }
    
    const durationMinutes = endMinutes - startMinutes;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    
    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours} hr`;
    return `${hours}h ${minutes}m`;
  };

  // Helper function to auto-suggest end time based on start time
  const handleStartTimeChange = (newStartTime) => {
    setStartTime(newStartTime);
    
    // Auto-set end time to 4 hours later if not set
    if (newStartTime && !endTime) {
      const [hours, minutes] = newStartTime.split(':').map(Number);
      const endHours = (hours + 4) % 24;
      const endTimeStr = `${endHours.toString().padStart(2, '0')}:00`;
      setEndTime(endTimeStr);
    }
    
    // Clear validation error
    if (formErrors.startTime) {
      setFormErrors(prev => ({...prev, startTime: ''}));
    }
  };

  // Add-ons state
  const [addIce, setAddIce] = useState(false);
  const [addGlassware, setAddGlassware] = useState(false);
  
  // UI state
  const [expandedCocktailIdx, setExpandedCocktailIdx] = useState(null);
  
  // Load catalog data from public/data/ using the same approach as other components
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        // console.log('🔄 Loading catalog data for CheckoutPage...');
        
        const [
          liquorData, 
          liqueurData, 
          juiceData, 
          sodaData, 
          syrupData, 
          champagneData,
          beerData,
          wineData,
          seltzerData
        ] = await Promise.all([
          getLiquorCatalog(),
          getLiqueurCatalog(), 
          getCrownCatalog(),
          getSodaCatalog(),
          getSyrupCatalog(),
          getChampagneCatalog(),
          getBeerCatalog(),
          getWineCatalog(),
          getSeltzerCatalog()
        ]);

        const catalogItemsById = {};
        
        // Combine all catalog items into a single lookup
        [liquorData, liqueurData, juiceData, sodaData, syrupData, champagneData, beerData, wineData, seltzerData]
          .flat()
          .forEach(item => {
            if (item && item.id) {
              catalogItemsById[item.id] = item;
            }
          });
          // console.log('✅ CheckoutPage loaded catalog data:', Object.keys(catalogItemsById).length, 'items');
        // console.log('📋 CheckoutPage catalog breakdown:', {
        //   liquor: liquorData?.length || 0,
        //   liqueur: liqueurData?.length || 0,
        //   juice: juiceData?.length || 0,
        //   soda: sodaData?.length || 0,
        //   syrup: syrupData?.length || 0,
        //   champagne: champagneData?.length || 0,
        //   beer: beerData?.length || 0,
        //   wine: wineData?.length || 0,
        //   seltzer: seltzerData?.length || 0
        // });
        // console.log('🔍 Sample catalog IDs:', Object.keys(catalogItemsById).slice(0, 10));
        setAllCatalogItemsById(catalogItemsById);
        setCatalogsLoaded(true);
      } catch (error) {
        console.error('❌ Error loading catalog data in CheckoutPage:', error);
        setCatalogsLoaded(true); // Allow component to render even if catalogs fail
      }
    };    loadCatalogs();
  }, []);
    // Process cocktails and add-ons data after hooks are set up
  let cocktails = location.state?.cocktails ?? (storedDataFromSession.orderData?.selectedItems?.cocktails || []);
  let otherAddOns = location.state?.otherAddOns ?? (
    storedDataFromSession.orderData?.selectedItems
      ? [
          ...(storedDataFromSession.orderData.selectedItems.beer || []),
          ...(storedDataFromSession.orderData.selectedItems.wine || []),
          ...(storedDataFromSession.orderData.selectedItems.spirits || []),
          ...(storedDataFromSession.orderData.selectedItems.champagne || []),
          ...(storedDataFromSession.orderData.selectedItems.seltzer || []),
          ...(storedDataFromSession.orderData.selectedItems.soda || []),
          ...(storedDataFromSession.orderData.selectedItems.snacks || []),
        ]
      : []
  );
  
  // Enhanced debug logging for cocktails and add-ons
  // console.log('🍸 CheckoutPage cocktails and add-ons processing:', {
  //   cocktailsSource: location.state?.cocktails ? 'location.state' : 'storedData',
  //   cocktailsCount: cocktails.length,
  //   addOnsSource: location.state?.otherAddOns ? 'location.state' : 'storedData',
  //   addOnsCount: otherAddOns.length,
  //   firstCocktail: cocktails[0],
  //   firstAddOn: otherAddOns[0]
  // });
  
  // Deduplication utility
  function deduplicateById(array) {
    if (!Array.isArray(array)) return [];
    const seen = new Map();
    array.forEach(item => {
      if (item && item.id) {
        // If already seen, merge quantities if possible
        if (seen.has(item.id)) {
          const prev = seen.get(item.id);
          // Merge quantities if both have qty/quantity
          const prevQty = prev.qty || prev.quantity || 0;
          const itemQty = item.qty || item.quantity || 0;
          seen.set(item.id, { ...prev, ...item, qty: prevQty + itemQty });
        } else {
          seen.set(item.id, { ...item });
        }
      }
    });
    return Array.from(seen.values());
  }

  // Deduplicate cocktails and add-ons by id
  cocktails = deduplicateById(cocktails).filter(c => (c.qty || c.quantity || 0) > 0);
  otherAddOns = deduplicateById(otherAddOns).filter(o => (o.qty || o.quantity || 0) > 0);
  
  // Memoize pricing calculation inputs to prevent infinite re-renders
  const memoizedCocktails = useMemo(() => 
    cocktails.map((c, idx) => ({ ...c, id: c.id || `cocktail-${idx}` })), 
    [cocktails]
  );
  
  const memoizedQuantities = useMemo(() => 
    cocktails.reduce((acc, c, idx) => {
      const id = c.id || `cocktail-${idx}`;
      acc[id] = c.qty || c.quantity || 1;
      return acc;
    }, {}), 
    [cocktails]
  );
  
  // Shared pricing calculation hook - using standardized approach
  const { grandTotals: pricingHookResults } = usePricingCalculation({ 
    cocktails: memoizedCocktails, 
    quantities: memoizedQuantities,
    catalogData: allCatalogItemsById 
  });

  // ─── 1. EVENT DETAILS ───
  // Event state is already defined above
  
  // ─── 2. ICE ADD-ON ───
  // Ice state is already defined above
  const ICE_PER_GUEST = 2; // lbs per guest
  const COST_PER_BAG = 6; // $ per 20 lb bag
  const totalIceLbs = guestCount * ICE_PER_GUEST;
  const bagsNeeded = Math.ceil(totalIceLbs / 20);
  const iceCost = addIce ? bagsNeeded * COST_PER_BAG : 0;
  // ─── 3. GLASSWARE ADD-ON ───
  // Glassware state is already defined above
  const GLASSWARE_COST = 30; // flat rate for rental
  const glasswareCost = addGlassware ? GLASSWARE_COST : 0;
  // ─── 4. ORDER SUMMARY ───
  const minServiceFee = listing.attributes.publicData.minServiceFee || 0;
  const perCocktailFee = listing.attributes.publicData.perCocktailFee || 0;
  const pricePerSession = listing.attributes.price && typeof listing.attributes.price.amount === 'number'
    ? listing.attributes.price.amount / 100
    : 0;
  // Debug: Log service fee values
  // console.log('🔧 Service fee debugging:', {
  //   minServiceFee,
  //   perCocktailFee,
  //   publicData: listing.attributes.publicData,
  //   listingPrice: listing.attributes.price
  // });

  // Precompute breakdown items
  // Use the base service charge from the listing price (EditListingWizard sets this)
  const baseServiceCharge = pricePerSession;
  const perCocktailCharge = listing.attributes.publicData.perCocktailFee || 0;

  // Calculate service charge: minimum + per-cocktail
  const cocktailsServiceCharge = cocktails.reduce(
    (sum, c) => sum + perCocktailFee * (c.qty || c.quantity || 1),
    0
  );
  const serviceCharge = minServiceFee + cocktailsServiceCharge;  // Debug: Log cocktail data structure for consistency analysis
  // console.log('🔍 CheckoutPage cocktails data structure:', cocktails);
  // console.log('📊 Sample cocktail structure:', cocktails[0]);
  // console.log('🧮 Pricing hook results:', pricingHookResults);  // Helper: Calculate cocktail cost using EXACT EnhancedCocktailCard logic
  function calculateCocktailCostFromCatalog(item, quantity, allCatalogItemsById) {
    // console.log(`🍹 Calculating cost for ${item.name || 'Cocktail'} - Quantity: ${quantity} (EnhancedCocktailCard method)`);
    
    // Helper: Normalize or infer catalogId from ingredient name if missing
    const inferCatalogId = (ingredient) => {
      if (ingredient.catalogId) return ingredient.catalogId;
      // Try to infer catalogId from name (e.g., 'Bacardi Superior White Rum' -> 'rum_bacardi')
      const name = (ingredient.name || '').toLowerCase();
      if (name.includes('bacardi')) return 'rum_bacardi';
      if (name.includes('lime juice')) return 'juice_lime';
      if (name.includes('lemon juice')) return 'juice_lemon';
      if (name.includes('patron')) return 'tequila_patron_silver';
      // Add more mappings as needed
      return null;
    };

    // Helper to normalize names to catalog keys (e.g., "Bacardi Superior White Rum" -> "rum_bacardi")
    function normalizeToCatalogKey(name) {
      if (!name) return '';
      let n = name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      // Try to match common catalog patterns
      if (n.includes('bacardi')) return 'rum_bacardi';
      if (n.includes('patron') || n.includes('patrón')) return 'tequila_patron_silver';
      if (n.includes('lime juice')) return 'juice_lime';
      if (n.includes('lemon juice')) return 'juice_lemon';
      if (n.includes('orange juice')) return 'juice_orange';
      if (n.includes('pineapple juice')) return 'juice_pineapple';
      if (n.includes('agave')) return 'custom-agave'; // custom, but for completeness
      // Generic fallback: try to match by removing spaces and underscores
      n = n.replace(/\s+/g, '_');
      return n;
    }

    let ingredientsTotal = 0;
    
    if (!item.ingredients) {
      // console.log('⚠️ No ingredients found');
      return 0;
    }

    item.ingredients.forEach((ingredient) => {
      // --- Begin EnhancedCocktailCard-style bottle math ---
      // Get bottle size and price
      let bottleMl = 750;
      let bottlePrice = 0;
      let catalogId = ingredient.catalogId;
      
      // Fallback: try to infer catalogId if missing or not found
      if (!catalogId || !allCatalogItemsById[catalogId]) {
        const normalizedKey = normalizeToCatalogKey(ingredient.name || ingredient.customIngredient?.name);
        if (allCatalogItemsById[normalizedKey]) {
          catalogId = normalizedKey;
        } else {
          const foundKey = Object.keys(allCatalogItemsById).find(key => {
            const item = allCatalogItemsById[key];
            return item.name && normalizeToCatalogKey(item.name) === normalizeToCatalogKey(ingredient.name || ingredient.customIngredient?.name);
          });
          if (foundKey) catalogId = foundKey;
        }
      }
      
      if (catalogId && allCatalogItemsById[catalogId]) {
        const catalogItem = allCatalogItemsById[catalogId];
        bottleMl = parseFloat(catalogItem.volume_ml || 750);
        bottlePrice = parseFloat(catalogItem.price || catalogItem.pricePerBottle || 0);
      } else if (ingredient.customIngredient) {
        const ci = ingredient.customIngredient;
        bottleMl = ci.bottleSizeMl || 1000;
        if (ci.price) {
          bottlePrice = parseFloat(ci.price);
        } else if (ci.pricePerUnit) {
          bottlePrice = parseFloat(ci.pricePerUnit);
        }
      } else if (ingredient.pricePerUnit) {
        bottlePrice = parseFloat(ingredient.pricePerUnit);
        bottleMl = 1000;
      }
        // Calculate cost based on ingredient type
      const qty = parseFloat(ingredient.qty || ingredient.amountPerCocktailOz || 0);
      const unit = ingredient.unit || 'oz';
      const isCustomIngredient = ingredient.isCustom || ingredient.customIngredient;
      const isWet = ingredient.customIngredient?.isWet !== false && ingredient.isWet !== false;
      
      let costForThisIngredient = 0;
      
      if (isCustomIngredient && !isWet) {
        // Custom dry ingredients: calculate by units (no bottle conversion)
        const totalUnitsNeeded = qty * quantity;
        costForThisIngredient = totalUnitsNeeded * bottlePrice;
        // console.log(`  📦 ${ingredient.name || 'Unknown'} (Custom Dry): ${qty}${unit} x ${quantity} = ${totalUnitsNeeded} units @ $${bottlePrice} = $${costForThisIngredient.toFixed(2)}`);
      } else if (isCustomIngredient && isWet) {
        // Custom wet ingredients: calculate total volume needed, convert to liters, round up
        const totalVolumeNeeded = qty * quantity;
        let volumeInLiters;
        if (unit === 'ml') {
          volumeInLiters = totalVolumeNeeded / 1000;
        } else if (unit === 'oz') {
          volumeInLiters = totalVolumeNeeded * 0.0295735; // oz to liters
        } else if (unit === 'liter' || unit === 'l') {
          volumeInLiters = totalVolumeNeeded;
        } else {
          volumeInLiters = totalVolumeNeeded * 0.0295735; // Default to oz conversion
        }
        const litersNeeded = Math.ceil(volumeInLiters);
        costForThisIngredient = litersNeeded * bottlePrice;
        // console.log(`  📦 ${ingredient.name || 'Unknown'} (Custom Wet): ${qty}${unit} x ${quantity} = ${volumeInLiters.toFixed(3)}L → ${litersNeeded}L @ $${bottlePrice} = $${costForThisIngredient.toFixed(2)}`);
      } else {
        // Catalog ingredients: use bottle-based calculation
        const amountPerCocktailMl = ingredient.amountPerCocktailOz
          ? ingredient.amountPerCocktailOz * 29.5735
          : (unit === 'ml' ? qty : unit === 'oz' ? qty * 29.5735 : unit === 'liter' || unit === 'l' ? qty * 1000 : qty);
        const totalAmountNeededMl = amountPerCocktailMl * quantity;
        const bottlesNeeded = Math.ceil(totalAmountNeededMl / bottleMl);
        costForThisIngredient = bottlesNeeded * bottlePrice;
        // console.log(`  📦 ${ingredient.name || 'Unknown'} (Catalog): ${qty}${unit} x ${quantity} = ${totalAmountNeededMl.toFixed(1)}ml, ${bottlesNeeded} bottles @ $${bottlePrice} = $${costForThisIngredient.toFixed(2)}`);
      }
      
      ingredientsTotal += costForThisIngredient;      
      // --- End ingredient cost calculation ---
    });
    
    // Return only the ingredient cost - service fees are handled separately
    // console.log(`✅ Updated ingredient calculation - Total ingredients cost: $${ingredientsTotal.toFixed(2)}`);
    return ingredientsTotal;
  }
  // Show loading state while catalogs are loading
  if (!catalogsLoaded) {
    return (
      <>
        <TopbarContainer />
        <div className={css.loadingContainer}>
          <p>Loading pricing data...</p>
        </div>
        <FooterContainer />
      </>
    );
  }  // Prepare items for rendering with calculated costs - using standardized calculation
  const itemsToRenderCocktails = cocktails.map(item => {
    const itemQty = item.qty || item.quantity || 1;
    
    // Use the standardized calculation function that matches ListingPage
    const calculatedIngredientCost = calculateCocktailCostFromCatalog(item, itemQty, allCatalogItemsById);
      // Use the cocktail's own service fee if available, otherwise fall back to listing's perCocktailFee
    const cocktailServiceFee = item.serviceFeePerCocktail || item.serviceFee || perCocktailFee;
    
    return {
      ...item,
      cost: calculatedIngredientCost, // Total cost for this cocktail for itemQty units
      serviceFeePerCocktail: cocktailServiceFee, // Use cocktail's own service fee
      qty: itemQty,
    };
  });

  let itemsToRenderOtherAddOns = otherAddOns.map(item => {
    const itemQty = item.qty || item.quantity || 1;
    let calculatedItemCost = 0;
    if (item.catalogId && allCatalogItemsById[item.catalogId]) {
      calculatedItemCost = (allCatalogItemsById[item.catalogId].price || 0) * itemQty;
    } else if (item.price != null) { // Support number or string
      const priceNum = typeof item.price === 'number' ? item.price : parseFloat(item.price);
      if (Number.isFinite(priceNum)) {
        calculatedItemCost = priceNum * itemQty;
      }
    } else if (item.ingredients && item.ingredients.length > 0) {
      // Fallback for complex add-ons structured like cocktails
      calculatedItemCost = calculateCocktailCostFromCatalog(item, itemQty, allCatalogItemsById);
    }
    return {
      ...item,
      cost: calculatedItemCost, // Total cost for this add-on for itemQty units
      qty: itemQty,
    };
  });  // Calculate total costs using our standardized approach
  const totalIngredientCost = itemsToRenderCocktails.reduce((sum, item) => sum + item.cost, 0);
  const totalServiceFees = itemsToRenderCocktails.reduce(
    (sum, item) => sum + (item.serviceFeePerCocktail || 0) * item.qty,
    0
  );

  // ================= Food Classification (for mixed food & drink bookings) =================
  // Heuristic: treat items as food if any of the following is true:
  //  - item.isFood flag present
  //  - item.category includes typical food keywords
  //  - listing is a chef/food related listing and item is not clearly alcohol/juice/ice
  const FOOD_KEYWORDS = ['food','meal','dish','menu','entree','entrée','appetizer','starter','snack','side','dessert','catering','plate','platter','taco','tacos','pizza','burger','sandwich','salad','wrap','bowl','rice','beans','noodle','pasta','sushi','steak','chicken','beef','pork','shrimp','salmon','fish','carne','asada','quesadilla','enchilada','fajita','carnitas'];
  const isFoodListing = (() => {
    const lc = (listing?.attributes?.publicData?.category || listing?.attributes?.publicData?.listingType || '')
      .toString()
      .toLowerCase();
    return ['chef','food','food-truck','food_truck','catering'].some(k => lc.includes(k));
  })();

  const classifyAddOnAsFood = item => {
    if (!item) return false;
    if (item.isFood) return true;
    const cat = (item.category || '').toLowerCase();
    const name = (item.name || '').toLowerCase();
    if (FOOD_KEYWORDS.some(k => cat.includes(k) || name.includes(k))) return true;
    // If listing is food-oriented and item not clearly a drink supply
    const drinkish = ['wine','beer','vodka','gin','rum','tequila','whiskey','whisky','bourbon','liqueur','seltzer','champagne','soda','juice','tonic','mix','syrup','ice'];
    if (isFoodListing && !drinkish.some(k => cat.includes(k) || name.includes(k))) return true;
    return false;
  };

  let foodItems = [];
  let nonFoodAddOns = [];
  itemsToRenderOtherAddOns.forEach(item => {
    if (classifyAddOnAsFood(item)) {
      foodItems.push({ ...item, isFood: true });
    } else {
      nonFoodAddOns.push(item);
    }
  });

  const totalFoodCost = foodItems.reduce((sum, item) => sum + item.cost, 0);
  const totalOtherAddOnsCost = nonFoodAddOns.reduce((sum, item) => sum + item.cost, 0);
  // Calculate subtotal before fees and tax
  const subtotalBeforeFees = totalIngredientCost + totalFoodCost + totalOtherAddOnsCost + baseServiceCharge + totalServiceFees + iceCost + glasswareCost;
  
  // Calculate 10% booking + delivery fee on the subtotal
  const tenPercentFee = subtotalBeforeFees * 0.1;
  
  // Calculate tax (8.5% on subtotal + booking fee)
  const TAX_RATE = 0.085; // 8.5% tax rate
  const taxableAmount = subtotalBeforeFees + tenPercentFee;
  const taxAmount = taxableAmount * TAX_RATE;
  
  // Calculate grand total with fees (no tax, handled by Stripe)
  const displayGrandTotalWithIndytend = subtotalBeforeFees + tenPercentFee;

  // console.log('💰 Cost breakdown - Subtotal:', subtotalBeforeFees, '10% Fee:', tenPercentFee, 'Tax:', taxAmount, 'Grand total:', displayGrandTotalWithIndytend);

  // Calculate drinksPerGuest
  const totalDrinks =
    itemsToRenderCocktails.reduce((sum, currentItem) => sum + currentItem.qty, 0) +
    itemsToRenderOtherAddOns.reduce((sum, currentItem) => sum + currentItem.qty, 0);
  const drinksPerGuest = guestCount > 0 ? totalDrinks / guestCount : 0;  // ─── 5. REQUEST TO BOOK ───
  // Retailer name resolution (consistent with other pages)
  const RETAILER_NAME =
    process.env.REACT_APP_LIQUOR_RETAILER_NAME ||
    (process.env.LIQUOR_STORE_ID ? process.env.LIQUOR_STORE_ID.replace(/[_-]/g, ' ') : '') ||
    'Cellars Fine Wine and Spirits';
  // Detect alcohol presence in order for disclosure
  const hasAlcoholInOrder = (() => {
    const alcoholKeywords = ['beer','wine','champagne','spirit','vodka','gin','rum','tequila','whiskey','whisky','bourbon','liqueur','seltzer','alcohol'];
    const checkItem = it => {
      const cat = (it.category || '').toLowerCase();
      const name = (it.name || '').toLowerCase();
      return alcoholKeywords.some(k => cat.includes(k) || name.includes(k)) || !!it.ingredients;
    };
    return itemsToRenderCocktails.length > 0 ||
      itemsToRenderOtherAddOns.some(checkItem);
  })();
  const handleRequestToBook = async () => {
  // (debug logging removed)
    // Guard: prevent booking own listing
    if (isOwnListing) {
      alert("You can't request to book your own listing. Please log in with a customer account or choose another listing.");
      return;
    }
    // Validate event details before proceeding
    if (!validateEventDetails()) {
      console.log('[DEBUG] handleRequestToBook: validateEventDetails() failed');
      // Scroll to the event details section to show errors
      const eventDetailsSection = document.querySelector(`.${css.eventDetails}`);
      if (eventDetailsSection) {
        eventDetailsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setSubmitting(true);

  // Create booking request transaction with proper ISO string formatting
  // IMPORTANT: new Date('YYYY-MM-DD') is parsed as UTC midnight, which can cause the
  // date to appear as the previous day when later rendered in a negative offset timezone (e.g. US timezones).
  // To avoid the off-by-one day issue, construct the Date using local date parts.
  const [year, month, day] = eventDate.split('-').map(Number);
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  const bookingStartDate = new Date(year, month - 1, day, startHour, startMinute, 0, 0); // local time
  const bookingEndDate = new Date(year, month - 1, day, endHour, endMinute, 0, 0); // local time

  // Convert to ISO strings for Sharetribe API compatibility (stored in UTC)
  const bookingStartISO = bookingStartDate.toISOString();
  const bookingEndISO = bookingEndDate.toISOString();
    // Create detailed inquiry message for the provider
    const inquiryMessage = `Event Booking Request

📅 Date: ${eventDate}
⏰ Time: ${startTime} - ${endTime}
📍 Location: ${eventLocation}
👥 Guest Count: ${guestCount}

🍸 Selected Cocktails:
${cocktails.map(cocktail => `- ${cocktail.name} (${cocktail.qty})`).join('\n')}

🍷 Additional Items:
${otherAddOns.map(addon => `- ${addon.name} (${addon.qty})`).join('\n')}

${addIce ? '🧊 Ice included' : ''}
${addGlassware ? '🥃 Glassware included' : ''}

💰 Total Amount: $${displayGrandTotalWithIndytend}

Please review this booking request and accept to proceed with payment.`;
    // Format cocktails for Sharetribe API (selectedCocktails)
    const selectedCocktails = cocktails.map(cocktail => ({
      id: cocktail.id,
      quantity: cocktail.quantity || 1
    }));
    // DEBUG: Log all arguments before building line items
    console.log('[DEBUG] buildLineItems args:', {
      listing,
      itemsToRenderCocktails,
      itemsToRenderOtherAddOns,
      baseServiceCharge,
      totalServiceFees,
      iceCost,
      glasswareCost,
      currency: 'USD'
    });
    // Build Sharetribe line items for the inquiry
    // Food items placeholder: if later added, pass separate array
    // Crown Produce ice vendor add-on (ensures line-item/ice is attributed to Crown Produce explicitly)
    const crownIceAddOn = addIce ? {
      id: 'ice_crown',
      name: 'Ice (Crown Produce)',
      category: 'ice',
      qty: 1,
      price: iceCost,
      vendor: 'Crown Produce',
      payeeGroup: 'crown'
    } : null;
    const addOnsForPricing = crownIceAddOn ? [...nonFoodAddOns, crownIceAddOn] : nonFoodAddOns;
  const lineItems = buildLineItemsDetailed(listing, {
      cocktails: itemsToRenderCocktails,
      addOns: addOnsForPricing,
      foodItems, // detected food items
      baseServiceCharge,
      totalServiceFees,
      iceCost: 0, // already represented as explicit add-on for Crown Produce
      glasswareCost,
      bookingFee: tenPercentFee,
      taxAmount,
      currency: 'USD',
      includeTax: true,
    });
    // (debug logging removed)
    // Guard: Prevent submission if no line items
    if (!lineItems || lineItems.length === 0) {
      console.log('[DEBUG] handleRequestToBook: lineItems is empty', lineItems);
      setSubmitting(false);
      setFormErrors(prev => ({ ...prev, general: 'Booking cannot be submitted: no pricing information was generated. Please check your selections and try again.' }));
      return;
    }
    // Prepare inquiry parameters for Sharetribe transaction with line items support
  const inquiryParams = {
      // Pass the SDK ID object directly (not the raw UUID string)
      listingId: listing.id,
      bookingStart: bookingStartISO,
      bookingEnd: bookingEndISO,
      // Reserve 1 seat in availability (seat count refers to concurrent bookings, not guest count)
      seats: 1,
      lineItems,
      selectedCocktails: selectedCocktails, // Optional, for reference
      protectedData: {
        // Primary (camelCase) keys used in app code
        inquiryMessage,
        bookingStart: bookingStartISO,
        bookingEnd: bookingEndISO,
        eventDetails: {
          location: eventLocation,
          guestCount: guestCount,
          date: eventDate,
          startTime: startTime,
          endTime: endTime
        },
        cocktailSelections: cocktails,
        // Persist food vs non-food separation for downstream pages
        foodItems: foodItems,
        otherAddOns: nonFoodAddOns,
        addOnSelections: {
          ice: addIce,
          glassware: addGlassware
        },
        vendors: crownIceAddOn ? { crown: { ice: iceCost, name: 'Crown Produce' } } : undefined,
        pricing: {
          totalIngredientCost,
          totalFoodCost,
          totalOtherAddOnsCost,
          baseServiceCharge,
          totalServiceFees,
          iceCost,
          glasswareCost,
          tenPercentFee,
          taxAmount,
          grandTotal: displayGrandTotalWithIndytend
        },
        seats: guestCount,

        // Kebab-case mirrors for Sharetribe email template aliases
        'inquiry-message': inquiryMessage,
        'booking-start': bookingStartISO,
        'booking-end': bookingEndISO,
        'event-details': {
          location: eventLocation,
          'guest-count': guestCount,
          date: eventDate,
          'start-time': startTime,
          'end-time': endTime
        },
        'cocktail-selections': cocktails,
        'other-add-ons': nonFoodAddOns,
        'debug-probe': 'checkout-inquire-v1'
      },
      // Canary to identify CheckoutPage initiation path in emails (camelCase too)
      debugProbe: 'checkout-inquire-v1',
    };
    try {
      // Get the transaction process
  // Force unified custom process alias regardless of listing stored value
  const processAlias = 'indytend-custom-booking/release-1';
      const processName = processAlias.split('/')[0];
      const process = getProcess(processName);
      // Use INQUIRE transition to create a booking request that goes to provider's inbox
      const transitionName = process.transitions.INQUIRE;
      const isPrivileged = process.isPrivileged(transitionName);

      // Enhanced logging for debugging transaction creation
      // console.log('🔍 CheckoutPage: About to create transaction with:', {
      //   inquiryParams,
      //   processAlias,
      //   transitionName,
      //   isPrivileged,
      //   listingId: listing?.id?.uuid,
      //   listingProcessAlias: listing?.attributes?.publicData?.transactionProcessAlias
      // });
      
      // Dispatch the inquiry transaction using the connected action
      const transaction = await onInitiateOrder(inquiryParams, processAlias, null, transitionName, isPrivileged);

      // console.log('✅ Inquiry transaction created successfully:', transaction);
      
      // Show inline confirmation for inquiry-only, else redirect
      if (processAlias.startsWith('default-inquiry')) {
        setSubmitted(true);
      } else {
        const orderDetailsPath = `/order/${transaction.id.uuid}`;
        history.push(orderDetailsPath);
      }
    } catch (error) {
      console.error('❌ Failed to create inquiry transaction:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.status,
        data: error.data,
        stack: error.stack
      });
      
      // More descriptive error message
      let errorMessage = 'Failed to submit booking request. ';
      // Special-case Sharetribe 409 when author === customer
      const firstErrCode = error?.data?.errors?.[0]?.code;
      if (error.status === 409 && firstErrCode === 'transaction-same-author-and-customer') {
        errorMessage = "You can't request to book your own listing. Please log in with a customer account or choose another listing.";
      }
      if (error.status === 403) {
        errorMessage += 'Permission denied - the transaction process may not support this action.';
      } else if (error.status === 400) {
        errorMessage += 'Invalid request data.';
      } else if (error.status === 404) {
        errorMessage += 'Transaction process not found.';
      } else {
        errorMessage += `Error: ${error.message || 'Unknown error'}`;
      }
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Inline success UI after inquiry submission
  if (submitted) {
    return (
      <>
        <TopbarContainer />
        <div className={css.successContainer}>
          <h2>🎉 Congratulations!</h2>
          <p>Your request has been sent. The provider will get back to you shortly.</p>
        </div>
        <FooterContainer />
      </>
    );
  }

  // ─── COMPONENT RENDER ───
  return (
    <>
      <TopbarContainer />
      <div className={css.root}>
        <button
          className={css.backButton}
          onClick={() => {
            // Always use the listing UUID for navigation
            const id = listing.id && listing.id.uuid ? listing.id.uuid : listing.id;
            const slug = listing.attributes.slug || '';
            // Use route helper to ensure correct param order
            history.push(`/l/${id}/${slug}`);
          }}
        >
          ← Go Back to Listing Page
        </button>         {/* 1. Event Details */}
         <section className={css.eventDetails}>
           <h2>Event Details</h2>
           <div className={css.eventDetailsContainer}>
             <div className={css.eventDetailsField}>
               <label>
                 Event Date
                 <div className={css.dateSelectionContainer}>
                   {/* Enhanced visual calendar */}
                   <EnhancedCalendar
                     providerId={listing?.relationships?.author?.data?.id?.uuid || listing?.author?.id?.uuid}
                     publicData={listing?.attributes?.publicData}
                     listingId={listing?.id?.uuid || listing?.id}
                     selectedDate={eventDate}
                     onDateSelect={(selectedDate) => {
                       setEventDate(selectedDate);
                       if (formErrors.eventDate) {
                         setFormErrors(prev => ({...prev, eventDate: ''}));
                       }
                     }}
                     onAvailabilityUpdate={(availabilityData) => {
                       setDisabledDates(availabilityData);
                     }}
                     minDate={getTodayDate()}
                     className={css.eventCalendar}
                     availabilityPlan={availabilityPlan}
                     allowConsecutiveBookableDays={true}
                   />
                 </div>
                 
                 {formErrors.eventDate && (
                   <span className={css.errorText}>{formErrors.eventDate}</span>
                 )}
               </label>
             </div>             <div className={css.eventDetailsField}>
               <label>
                 Start Time
                 <div className={css.timeFieldContainer}>
                   <select 
                     value={startTime} 
                     onChange={e => handleStartTimeChange(e.target.value)}
                     required 
                     className={formErrors.startTime ? css.errorInput : ''}
                     data-time-select
                   >
                     {timeOptions.map((option) => (
                       <option key={option.value} value={option.value}>
                         {option.label}
                       </option>
                     ))}
                   </select>
                 </div>
                 {formErrors.startTime && (
                   <span className={css.errorText}>{formErrors.startTime}</span>
                 )}
               </label>
             </div>
             
             <div className={css.eventDetailsField}>
               <label>
                 End Time
                 <div className={css.timeFieldContainer}>
                   <select 
                     value={endTime} 
                     onChange={e => {
                       setEndTime(e.target.value);
                       if (formErrors.endTime) {
                         setFormErrors(prev => ({...prev, endTime: ''}));
                       }
                     }} 
                     required 
                     className={formErrors.endTime ? css.errorInput : ''}
                     data-time-select
                   >
                     {timeOptions.map((option) => (
                       <option key={option.value} value={option.value}>
                         {option.label}
                       </option>
                     ))}
                   </select>
                   {startTime && endTime && (
                     <div className={css.durationDisplay}>
                       Duration: {calculateDuration(startTime, endTime)}
                     </div>
                   )}
                 </div>
                 {formErrors.endTime && (
                   <span className={css.errorText}>{formErrors.endTime}</span>
                 )}
               </label>
             </div>
               <div className={css.eventDetailsField}>
               <label>
                 Guest Count
                 <div className={css.guestCountButtons}>
                   <button 
                     type="button"
                     className={css.guestCountBtn}
                     onClick={() => setGuestCount(prev => Math.max(1, prev - 1))}
                     aria-label="Decrease guest count"
                   >
                   </button>
                   <input 
                     type="number" 
                     min="1" 
                     value={guestCount} 
                     onChange={e => {
                       const value = Math.max(1, Number(e.target.value));
                       setGuestCount(value);
                       if (formErrors.guestCount) {
                         setFormErrors(prev => ({...prev, guestCount: ''}));
                       }
                     }} 
                     className={`${css.guestCountInput} ${formErrors.guestCount ? css.errorInput : ''}`}
                     required 
                   />
                   <button 
                     type="button"
                     className={css.guestCountBtn}
                     onClick={() => setGuestCount(prev => prev + 1)}
                     aria-label="Increase guest count"
                   >
                   </button>
                 </div>
                 {formErrors.guestCount && (
                   <span className={css.errorText}>{formErrors.guestCount}</span>
                 )}
               </label>
             </div>
             
             <div className={`${css.eventDetailsField} ${css.eventDetailsLocationField}`}>
               <label>
                 Event Location
                 <input 
                   type="text" 
                   placeholder="Phoenix area only for now — enter the complete event address (more cities coming soon)" 
                   value={eventLocation} 
                   onChange={e => {
                     setEventLocation(e.target.value);
                     if (formErrors.eventLocation) {
                       setFormErrors(prev => ({...prev, eventLocation: ''}));
                     }
                   }} 
                   required 
                   className={formErrors.eventLocation ? css.errorInput : ''}
                 />
                 {formErrors.eventLocation && (
                   <span className={css.errorText}>{formErrors.eventLocation}</span>
                 )}
               </label>
             </div>
           </div>
           
           {guestCount > 50 && (
             <div className={css.warning}>
               For events over 50 guests, we recommend hiring more than one bartender for optimal service.
             </div>
           )}
         </section>

         {/* 2. Ice Add-On */}
         <section className={css.iceSection}>
           <h2>Ice Add-On</h2>
           <p>Suggested: {totalIceLbs} lbs ({bagsNeeded} × 20lb bags)</p>
           <button onClick={() => setAddIce(!addIce)}>
             {addIce ? 'Remove Ice' : `Add Ice ($${bagsNeeded * COST_PER_BAG})`}
           </button>
         </section>

         {/* 3. Glassware Add-On (removed per request; logic retained for now to keep cost row stable) */}
         {/* <section className={css.glasswareSection}>
           <h2>Glassware Rental</h2>
           <p>Flat rate: ${GLASSWARE_COST}</p>
           <button onClick={() => setAddGlassware(!addGlassware)}>
             {addGlassware ? 'Remove Glassware' : `Add Glassware ($${GLASSWARE_COST})`}
           </button>
         </section> */}

         {/* 4. Order Summary */}         <section className={css.summarySection}>
           <h2>Order Summary</h2>
           <div className={css.summaryBox}>
             {/* Drink Order breakdown */}
             <div className={css.summaryRow} style={{alignItems:'flex-start',flexDirection:'column',gap:0}}>
               <span className={css.summaryLabel} style={{marginBottom:4}}>Drink Order</span>
               <div style={{width:'100%'}}>
                 {itemsToRenderCocktails.length === 0 && itemsToRenderOtherAddOns.length === 0 && (
                   <p style={{color: 'red', marginLeft:0}}>No drinks found in your order. Please return to the menu and select items.</p>
                 )}
                 {/* Cocktails */}
                 {itemsToRenderCocktails.length > 0 && itemsToRenderCocktails.map((item, idx) => (
                   <div key={`alcohol-ct-${idx}`} className={css.cocktailOrderContainer}>                     <div className={css.drinkOrderHeader}>
                       <span className={css.drinkOrderName}>{item.name}</span>
                       <span className={css.drinkOrderPrice}>${((item.cost || 0) + ((item.serviceFeePerCocktail || 0) * (item.qty || 1))).toFixed(2)}</span>
                     </div><div className={css.drinkOrderQtyRow}>
                       Qty: {item.qty}
                     </div>                     <div className={css.serviceFeeRow} style={{marginLeft:8, color:'#888', fontSize:'0.95em'}}>
                       Ingredient Cost: ${(item.cost || 0).toFixed(2)}
                     </div>
                     <div className={css.serviceFeeRow} style={{marginLeft:8, color:'#888', fontSize:'0.95em'}}>
                       Service Charge: ${((item.serviceFeePerCocktail || 0) * (item.qty || 1)).toFixed(2)}
                       {item.qty > 1 ? ` ($${(item.serviceFeePerCocktail || 0).toFixed(2)} each)` : ''}
                     </div>
                     {/* Removed Ingredient/alcohol cost breakdown */}
                     {/* Submenu for ingredients and service charge breakdown */}
                     {expandedCocktailIdx === idx && item.ingredients.length > 0 && (
                       <div className={css.bottleBreakdownContainer}>
                         <div className={css.bottleBreakdownHeading}>Breakdown</div>
                         <ul className={css.bottleBreakdownList}>                           {item.ingredients.map((ing, i) => {
                             const { bottleMl, bottlePrice } = getBottleInfo(ing, allCatalogItemsById);
                             const qty = parseFloat(ing.qty || ing.amountPerCocktailOz || 0);
                             const unit = ing.unit || 'oz';
                             const amountPerCocktailMl = ing.amountPerCocktailOz
                               ? ing.amountPerCocktailOz * 29.5735
                               : convertToMl(qty, unit);
                             const totalAmountNeededMl = amountPerCocktailMl * item.qty;
                             const priceStr = bottlePrice !== null ? `$${bottlePrice.toFixed(2)}` : '';
                             const nameStr = ing.name || '';
                             // Detect kind for display
                             // Resolve catalog entry: prefer explicit catalogId, else try name-based lookup
                             let entry = null;
                             if (ing.catalogId && allCatalogItemsById[ing.catalogId]) {
                               entry = allCatalogItemsById[ing.catalogId];
                             } else {
                               const norm = (name) => {
                                 if (!name) return '';
                                 // normalize to a loose key similar to Listing/Transaction
                                 return name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, '_');
                               };
                               const ingNameNorm = norm(ing.name || ing.customIngredient?.name);
                               if (ingNameNorm && allCatalogItemsById[ingNameNorm]) {
                                 entry = allCatalogItemsById[ingNameNorm];
                               } else {
                                 const foundKey = Object.keys(allCatalogItemsById).find(k => {
                                   const it = allCatalogItemsById[k];
                                   return it && it.name && norm(it.name) === ingNameNorm;
                                 });
                                 if (foundKey) entry = allCatalogItemsById[foundKey];
                               }
                             }
                             let qtyStr = '';
                             if (entry && (entry.volume_ml || entry.unitLabel)) {
                               if (entry.volume_ml) {
                                 const capMl = parseFloat(entry.volume_ml) || bottleMl || 750;
                                 const bottlesNeeded = Math.ceil(totalAmountNeededMl / capMl);
                                 qtyStr = `${bottlesNeeded} bottle${bottlesNeeded !== 1 ? 's' : ''}`;
                               } else if (entry.unitLabel) {
                                 const lbl = String(entry.unitLabel).toLowerCase();
                                 const ct = lbl.match(/(\d+(?:\.\d+)?)\s*ct/);
                                 const weight = lbl.match(/(\d+(?:\.\d+)?)\s*(lb|lbs|oz|g|kg)/);
                                 if (ct) {
                                   const unitsPerPack = parseFloat(ct[1]) || 1;
                                   const totalUnits = qty * (item.qty || 1);
                                   const packs = Math.ceil(totalUnits / unitsPerPack);
                                   qtyStr = `${packs} pack${packs !== 1 ? 's' : ''}`;
                                 } else if (weight) {
                                   const amt = parseFloat(weight[1]);
                                   const u = weight[2];
                                   let gramsPerPack = 0;
                                   if (/lb|lbs/.test(u)) gramsPerPack = amt * 453.59237;
                                   else if (/oz/.test(u)) gramsPerPack = amt * 28.349523125;
                                   else if (/kg/.test(u)) gramsPerPack = amt * 1000;
                                   else gramsPerPack = amt;
                                   // convert ingredient qty to grams
                                   let grams = qty;
                                   if (unit === 'kg') grams = qty * 1000;
                                   else if (unit === 'lb' || unit === 'lbs') grams = qty * 453.59237;
                                   else if (unit === 'oz') grams = qty * 28.349523125;
                                   // else assume grams
                                   const totalGrams = grams * (item.qty || 1);
                                   const packs = Math.ceil(totalGrams / (gramsPerPack || 1));
                                   qtyStr = `${packs} pack${packs !== 1 ? 's' : ''}`;
                                 } else {
                                   // Unknown unitLabel -> assume at least one pack rather than misleading bottles
                                   const packs = Math.max(1, Math.ceil((qty * (item.qty || 1)) / 1));
                                   qtyStr = `${packs} pack${packs !== 1 ? 's' : ''}`;
                                 }
                               }
                             } else {
                               // Fallback: bottles by ml
                               const bottlesNeeded = Math.ceil(totalAmountNeededMl / (bottleMl || 750));
                               qtyStr = `${bottlesNeeded} bottle${bottlesNeeded !== 1 ? 's' : ''}`;
                             }
                             return (
                               <li key={i} className={css.bottleBreakdownItem}>
                                 <span style={{minWidth:90, fontWeight:500, color:'#1d3557'}}>{priceStr}</span>
                                 <span style={{flex:1, marginLeft:8, marginRight:8}}>{nameStr}</span>
                                 <span style={{fontWeight:500, color:'#457b9d'}}>{qtyStr}</span>
                               </li>
                             );
                           })}
                           {/* Service charge per cocktail (bottom, per cocktail only) */}                           <li className={css.bottleBreakdownItem}>
                             <span style={{minWidth:90, fontWeight:500, color:'#1d3557'}}>
                               ${(item.serviceFeePerCocktail || 0).toFixed(2)}
                             </span>
                             <span style={{flex:1, marginLeft:8, marginRight:8}}>
                               Service Charge per cocktail
                             </span>
                             <span style={{fontWeight:500, color:'#457b9d'}}></span>
                           </li>
                         </ul>
                       </div>
                     )}
                     {/* Expand/collapse button for ingredients */}
                     {item.ingredients.length > 0 && (
                       <button className={css.ingredientToggleBtn} onClick={() => setExpandedCocktailIdx(expandedCocktailIdx === idx ? null : idx)}>
                         {expandedCocktailIdx === idx ? 'Hide Ingredients' : 'Show Ingredients'}
                       </button>
                     )}
                   </div>
                 ))}
                 {/* Other Add-ons */}
                 {itemsToRenderOtherAddOns.length > 0 && itemsToRenderOtherAddOns.map((item, idx) => (
                   <div key={`alcohol-ao-${idx}`} className={css.addOnOrderContainer}>                     <div className={css.drinkOrderHeader}>
                       <span className={css.drinkOrderName}>{item.name}</span>
                       <span className={css.drinkOrderPrice}>${(item.cost || 0).toFixed(2)}</span>
                     </div>
                     <div className={css.drinkOrderQtyRow}>Qty: {item.qty}</div>
                   </div>
                 ))}               </div>
             </div>             <div className={css.summaryRow}>
              {/* Show separate Food Total if any food items detected */}
              {totalFoodCost > 0 && (
                <>
                  <span className={css.summaryLabel}>Food Total</span>
                  <span className={css.summaryValue}>${(totalFoodCost || 0).toFixed(2)}</span>
                </>
              )}
            </div>
            <div className={css.summaryRow}>
              <span className={css.summaryLabel}>Drink Total</span>
              <span className={css.summaryValue}>${(totalIngredientCost + totalOtherAddOnsCost || 0).toFixed(2)}</span>
             </div>

             <div className={css.summaryRow}>
               <span className={css.summaryLabel}>Service Charge Total</span>
               <span className={css.summaryValue}>${(baseServiceCharge + totalServiceFees || 0).toFixed(2)}</span>
             </div>
             <div className={css.summarySubRow} style={{marginLeft: '20px', fontSize: '0.9em', color: '#666'}}>
               Base ${(baseServiceCharge || 0).toFixed(2)} + Per Cocktail Service Charge ${(totalServiceFees || 0).toFixed(2)}
             </div>

             <div className={css.summaryRow}>
               <span className={css.summaryLabel}>Ice Cost</span>
               <span className={css.summaryValue}>${(iceCost || 0).toFixed(2)}</span>
             </div>

             <div className={css.summaryRow}>
               <span className={css.summaryLabel}>Glassware Cost</span>
               <span className={css.summaryValue}>${(glasswareCost || 0).toFixed(2)}</span>
             </div>

             <div className={css.summaryRow}>
               <span className={css.summaryLabel}>Booking + Delivery Fee</span>
               <span className={css.summaryValue}>${(tenPercentFee || 0).toFixed(2)}</span>
             </div>
             <div className={css.grandTotalRow}>
               Total
               <span style={{float:'right'}}>${(displayGrandTotalWithIndytend || 0).toFixed(2)}</span>
             </div>
             <div className={css.perGuestRow}>
               Total per Guest: ${guestCount > 0 ? ((displayGrandTotalWithIndytend || 0) / guestCount).toFixed(2) : '0.00'}
             </div>
             <div className={css.drinksPerGuestRow}>
               Drinks per Guest: {(drinksPerGuest || 0).toFixed(2)}
             </div>
              {hasAlcoholInOrder && (
                <div className={css.disclosureNote}>
                  Alcohol is sold and fulfilled by {RETAILER_NAME}. Adult (21+) ID required on delivery.
                </div>
              )}
           </div>
         </section>         {/* 5. Request to Book CTA */}
         <div className={css.ctaContainer}>
           <button 
             className={`${css.requestButton} ${(!eventDate || !startTime || !endTime || !eventLocation.trim()) ? css.requestButtonDisabled : ''}`}
             onClick={handleRequestToBook}
              disabled={!eventDate || !startTime || !endTime || !eventLocation.trim() || isOwnListing}
           >
              {isOwnListing
                ? "You can't book your own listing"
                : (!eventDate || !startTime || !endTime || !eventLocation.trim()) 
                  ? 'Complete Event Details to Continue' 
                  : 'Request to Book'
              }
           </button>
            {isOwnListing && (
              <div style={{ color: '#b00020', marginTop: 8 }}>
                You are the author of this listing. Use a customer account to submit a request.
              </div>
            )}
         </div>
      </div>
      <FooterContainer />
    </>
  );
};

// Validate props passed via router state
CheckoutPage.propTypes = {
  listing: PropTypes.object.isRequired,
  cocktails: PropTypes.array,
  otherAddOns: PropTypes.array,
  onInitiateOrder: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
};

const mapDispatchToProps = dispatch => ({
  onInitiateOrder: (orderParams, processAlias, transactionId, transitionName, isPrivileged) =>
    dispatch(initiateOrder(orderParams, processAlias, transactionId, transitionName, isPrivileged)),
});

const mapStateToProps = state => ({
  currentUser: state.user?.currentUser || null,
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(CheckoutPage);

// Helper: get bottle size and price from catalog (default 750ml, null price)
function getBottleInfo(ingredient, catalogData = {}) {
  // Special case: custom item with pricePerUnit, price, or customPrice
  if (ingredient.type === 'custom') {
    // Use pricePerUnit for bottle-based pricing
    let price = null;
    if (ingredient.pricePerUnit) {
      price = parseFloat(ingredient.pricePerUnit);
    } else if (ingredient.price) {
      price = parseFloat(ingredient.price);
    } else if (ingredient.customPrice) {
      price = parseFloat(ingredient.customPrice);
    }
    if (price !== null) {
      ingredient.pricePerUnit = price;
      return {
        bottleMl: ingredient.bottleSizeMl || 1000,
        bottlePrice: price
      };
    }
    return { bottleMl: ingredient.bottleSizeMl || 1000, bottlePrice: null };
  }
  let bottleMl = 750;
  let bottlePrice = null;
  if (ingredient.catalogId) {
    const entry = catalogData[ingredient.catalogId];
    if (entry) {
      if (entry.volume_ml) bottleMl = parseFloat(entry.volume_ml);
      // Use price directly if present
      if (entry.price !== undefined && entry.price !== null && entry.price !== '') {
        bottlePrice = parseFloat(entry.price);
      } else {          // Try unitPrice, pricePerUnit        let rawPrice = entry.unitPrice ?? entry.pricePerUnit ?? null;
        if (typeof rawPrice === 'string') rawPrice = rawPrice.replace(/\$/g, '').trim();
        if (rawPrice !== null && rawPrice !== undefined && rawPrice !== '') {
          const parsed = parseFloat(rawPrice);
          if (!isNaN(parsed)) bottlePrice = parsed;
        }
      }
    }
  }// Fallback: check ingredient itself for pricePerUnit (but NOT for custom items)
  if (bottlePrice === null && ingredient.type !== 'custom') {
    if (ingredient.pricePerUnit) {
      bottlePrice = parseFloat(ingredient.pricePerUnit);
    }
  }  // --- FIX: If this is a custom ingredient but type is missing, check for isCustom flag ---
  if ((ingredient.isCustom || ingredient.customIngredient) && !ingredient.type) {
    let price = null;
    if (ingredient.pricePerUnit) {
      price = parseFloat(ingredient.pricePerUnit);
    } else if (ingredient.price) {
      price = parseFloat(ingredient.price);
    } else if (ingredient.customPrice) {
      price = parseFloat(ingredient.customPrice);
    }
    if (price !== null) {
      return {
        bottleMl: 1000,
        bottlePrice: price
      };
    }
    return { bottleMl: 1000, bottlePrice: null };
  }
  return { bottleMl, bottlePrice };
}

// Helper: convert any unit to ml
function convertToMl(quantity, unit) {
  if (!quantity) return 0;
  switch ((unit||'').toLowerCase()) {
    case 'ml': return quantity;
    case 'oz': return quantity * 29.5735;
    case 'liter': case 'l': return quantity * 1000;
    default: return quantity;
  }
}
